import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Box, Button, Paper, Stack, Tab, Tabs, Tooltip, Typography } from '@mui/material';
import { CheckCircleOutline, FactCheckOutlined } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import financeService from '../../../services/financeService';
import { CashCount } from '../../../types/finance';
import { useAuth } from '../../../context/AuthContext';
import { useApprovalRule } from '../../../hooks/useApprovalRule';
import { useTimezone } from '../../../hooks/useTimezone';
import { useModulePermissions } from '../../../hooks/useModulePermissions';
import { formatPaisa } from '../../../utils/paisa';
import EmptyState from '../../common/EmptyState';
import ReasonDialog from '../../common/ReasonDialog';
import Amount from '../ledger/Amount';
import { CashDifference } from './CashCountStatus';

type View = 'PENDING' | 'REJECTED';

/**
 * Cash-count differences waiting for a second person, and those sent back.
 * Whoever counted sees theirs here too; only someone else can decide.
 */
const CashCountApprovalsPanel: React.FC = () => {
  const { decodedToken } = useAuth();
  const { ownBlocked } = useApprovalRule();
  const { formatDate, formatDateTime } = useTimezone();
  const { canWrite } = useModulePermissions();
  const { enqueueSnackbar } = useSnackbar();
  const [view, setView] = useState<View>('PENDING');
  const [items, setItems] = useState<CashCount[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [rejecting, setRejecting] = useState<CashCount | null>(null);
  const canDecide = canWrite('finance_journal_approve');

  const load = useCallback(async () => {
    try {
      setItems((await financeService.listCashCounts({ status: view, page: 1, limit: 50 })).items);
      setError(null);
    } catch (e: any) {
      setError(e?.message || 'Could not load cash counts.');
    }
  }, [view]);

  useEffect(() => { load(); }, [load]);

  const approve = async (c: CashCount) => {
    setBusyId(c._id);
    try {
      const res = await financeService.approveCashCount(c._id);
      enqueueSnackbar(`Approved and posted as ${res.entryNo}`, { variant: 'success' });
      await load();
    } catch (e: any) {
      enqueueSnackbar(e?.message || 'Could not approve this difference.', { variant: 'error' });
    } finally {
      setBusyId(null);
    }
  };

  const figure = (label: string, value: React.ReactNode) => (
    <Box>
      <Typography variant="caption" color="text.secondary" display="block">{label}</Typography>
      {value}
    </Box>
  );

  return (
    <Box>
      <Tabs value={view} onChange={(_, v) => setView(v)} sx={{ mb: 2, minHeight: 36, '& .MuiTab-root': { textTransform: 'none', minHeight: 36 } }}>
        <Tab value="PENDING" label="Waiting for approval" />
        <Tab value="REJECTED" label="Sent back" />
      </Tabs>

      {error && <Alert severity="error">{error}</Alert>}
      {items && items.length === 0 && (
        <EmptyState
          icon={view === 'PENDING' ? <CheckCircleOutline /> : <FactCheckOutlined />}
          title={view === 'PENDING' ? 'Nothing waiting' : 'Nothing sent back'}
          description={view === 'PENDING' ? 'Till counts that differ from the books appear here.' : 'Counts sent back appear here with the reason; the till can be counted again.'}
          dense
        />
      )}

      <Stack spacing={2}>
        {items?.map(c => {
          const own = ownBlocked(c.countedBy === decodedToken?.userId);
          return (
            <Paper key={c._id} variant="outlined" sx={{ borderRadius: 2.5, overflow: 'hidden' }}>
              <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={2} sx={{ px: 2.5, py: 2 }}>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="subtitle2" fontWeight={700}>{c.accountName} · {formatDate(c.day)}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Counted by {c.countedByName ?? 'Unknown'} {formatDateTime(c.createdAt)}
                  </Typography>
                </Box>
                <CashDifference paisa={c.differencePaisa} sx={{ fontSize: '1.25rem', fontWeight: 700 }} />
              </Stack>
              <Stack direction="row" spacing={4} sx={{ px: 2.5, pb: 2, flexWrap: 'wrap', rowGap: 1.5 }}>
                {figure('Books expect', <Amount paisa={c.expectedPaisa} blankZero={false} fontWeight={600} />)}
                {figure('Counted', <Amount paisa={c.countedPaisa} blankZero={false} fontWeight={600} />)}
                {figure('Explanation', <Typography variant="body2">{c.note}</Typography>)}
              </Stack>
              {view === 'REJECTED' ? (
                <Alert severity="warning" sx={{ borderRadius: 0 }}>
                  Sent back by {c.decidedByName ?? 'Unknown'}: {c.rejectionReason}
                </Alert>
              ) : canDecide && (
                <Stack direction="row" justifyContent="flex-end" spacing={1} sx={{ px: 2.5, py: 1.5, borderTop: 1, borderColor: 'divider' }}>
                  <Tooltip title={own ? 'You counted this till — someone else must decide.' : ''}>
                    <span>
                      <Button color="inherit" onClick={() => setRejecting(c)} disabled={own || busyId === c._id}>Send back</Button>
                    </span>
                  </Tooltip>
                  <Tooltip title={own ? 'You counted this till — someone else must approve.' : ''}>
                    <span>
                      <Button variant="contained" disableElevation onClick={() => approve(c)} disabled={own || busyId === c._id}>
                        {busyId === c._id ? 'Posting…' : 'Approve and post'}
                      </Button>
                    </span>
                  </Tooltip>
                </Stack>
              )}
            </Paper>
          );
        })}
      </Stack>

      <ReasonDialog
        open={!!rejecting}
        onClose={() => setRejecting(null)}
        title="Send this count back?"
        consequence="Nothing posts. The till can be counted again for that day."
        facts={rejecting ? [
          { label: 'Till', value: `${rejecting.accountName} · ${formatDate(rejecting.day)}` },
          { label: 'Difference', value: `${rejecting.differencePaisa > 0 ? 'Over' : 'Short'} ${formatPaisa(Math.abs(rejecting.differencePaisa))}` },
        ] : []}
        confirmText="Send back"
        confirmColor="warning"
        reasonPlaceholder="What should be checked?"
        onConfirm={async reason => {
          await financeService.rejectCashCount(rejecting!._id, reason);
          enqueueSnackbar('Count sent back', { variant: 'success' });
          await load();
        }}
      />
    </Box>
  );
};

export default CashCountApprovalsPanel;
